/**
 * Episodes List Page
 *
 * Main episodes management page with filtering, pagination, and CRUD actions.
 *
 * Acceptance Criteria: Task 9 - AC 4
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import {
  useEpisodes,
  useCreateEpisode,
  useDeleteEpisode,
  useActivateEpisode,
  useDeactivateEpisode,
  fetchEpisode,
  type EpisodeFilters,
  type EpisodeListItem,
  type EpisodeStatus,
} from '../services/episodesApi';
import { createSession, finishSession } from '../services/episodeSessionApi';
import {
  exportEpisodeToJson,
  downloadJson,
  sanitizeFilename,
  validateEpisodeImport,
  transformImportToFormData,
  transformLocalizationForApi,
} from '../utils/episodeExportImport';
import { uploadEpisodeAsset } from '../services/imageUploadApi';
import { useCreateDisplayLocalization } from '../services/displayLocalizationApi';
import { EpisodeNavigation } from '../components/EpisodeNavigation';
import { CreateEpisodeButton } from '../components/CreateEpisodeButton';
import {
  getEpisodeCreationPreference,
  setEpisodeCreationPreference,
} from '../services/preferences';
import { type EpisodeCreationMethod } from '../types/episode.types';
import { useInitialData, useTagsFromInitialData } from 'contexts/InitialDataContext';
import { useLanguage } from 'contexts/LanguageContext';
import { getSkillLevelEnglishName, getLearningGoalEnglishName } from 'services/initialDataApi';
import { formatRelativeTime } from 'utils/dateTime';
import logger from 'utils/logger';
import { apiGet } from 'services/api';

/**
 * API error response structure from BusinessException
 */
interface ApiErrorResponse {
  error: {
    type: string;
    message: string;
    code: string;
    extra?: {
      episode_id?: string;
      existing_session_id?: string;
    };
  };
}

/**
 * State for the session conflict dialog
 */
interface SessionConflictState {
  show: boolean;
  episodeId: string | null;
  existingSessionId: string | null;
  episodeName: string;
}

export function EpisodesListView() {
  const navigate = useNavigate();

  // Use global language from context (selected in header)
  const { selectedLanguage } = useLanguage();

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Story 3.9.15: Default to English language filter
  const [filters, setFilters] = useState<EpisodeFilters>({
    page: 1,
    limit: 50,
    languageCode: selectedLanguage,
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [episodeToDelete, setEpisodeToDelete] = useState<EpisodeListItem | null>(null);

  // Story 3.9.15: Episodes are language-specific, no language selection needed
  const [isTryButtonLoading, setIsTryButtonLoading] = useState<string | null>(null);

  // Session conflict dialog state - shown when active session already exists
  const [sessionConflict, setSessionConflict] = useState<SessionConflictState>({
    show: false,
    episodeId: null,
    existingSessionId: null,
    episodeName: '',
  });

  // Story 11.19: Episode export state - track which episode is being exported
  const [exportingEpisodeId, setExportingEpisodeId] = useState<string | null>(null);

  // Import from JSON state
  const [isImporting, setIsImporting] = useState(false);
  const importFileInputRef = useRef<HTMLInputElement>(null);

  // Load episode creation preference from localStorage
  const [creationMethod, setCreationMethod] = useState<EpisodeCreationMethod>(() =>
    getEpisodeCreationPreference()
  );

  // State for learning goal filter (filters tags dropdown)
  const [selectedLearningGoalId, setSelectedLearningGoalId] = useState<string | undefined>(
    undefined
  );

  const { data: episodes, isLoading, error } = useEpisodes(filters);
  const tags = useTagsFromInitialData();
  const { state: initialDataState } = useInitialData();
  const skillLevels = initialDataState.data?.skillLevels || [];
  const learningGoals = initialDataState.data?.learningGoals || [];

  // Filter tags by selected learning goal
  const filteredTags = selectedLearningGoalId
    ? tags.filter((tag) => tag.learningGoalIds.includes(Number(selectedLearningGoalId)))
    : tags;
  const deleteEpisodeMutation = useDeleteEpisode();
  const activateEpisodeMutation = useActivateEpisode();
  const deactivateEpisodeMutation = useDeactivateEpisode();
  const createEpisodeMutation = useCreateEpisode();
  const createLocalizationMutation = useCreateDisplayLocalization();

  // Debounce search with 300ms delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
    setFilters((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearch]);

  // Sync filters when global language changes
  useEffect(() => {
    setFilters((prev) => ({ ...prev, languageCode: selectedLanguage, page: 1 }));
    setPage(1);
  }, [selectedLanguage]);

  const handleFilterChange = (
    key: keyof EpisodeFilters,
    value: string | number | string[] | number[] | undefined
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' ? undefined : value,
      page: 1,
    }));
    setPage(1);
  };

  // Handler for learning goal filter change - also clears selected tags
  const handleLearningGoalChange = (learningGoalId: string | undefined) => {
    setSelectedLearningGoalId(learningGoalId);
    // Clear selected tags when learning goal changes
    setFilters((prev) => ({
      ...prev,
      tags: undefined,
      page: 1,
    }));
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleExecuteCreation = (method: EpisodeCreationMethod) => {
    if (method === 'manual') {
      navigate('/episodes/new');
    } else if (method === 'generate') {
      navigate('/episodes/generate');
    } else if (method === 'import') {
      importFileInputRef.current?.click();
    }
  };

  const handleMethodChange = (method: EpisodeCreationMethod) => {
    setCreationMethod(method);
    if (method !== 'import') {
      setEpisodeCreationPreference(method);
    }
  };

  /** Handle full episode import from JSON: create episode, re-upload images, create localizations */
  const handleImportFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    // Parse and validate JSON
    let exportData;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const result = validateEpisodeImport(data);
      if (!result.valid) {
        toast.error(result.error || 'Invalid episode data');
        return;
      }
      exportData = result.data!;
    } catch {
      toast.error('Invalid JSON file. Please check the file format.');
      return;
    }

    setIsImporting(true);
    const toastId = toast.loading('Importing episode...');

    try {
      const formData = transformImportToFormData(exportData, tags);
      const newEpisodeId = crypto.randomUUID();

      // 1. Re-upload images via backend (backend downloads from source URL)
      const imageUploads: Promise<{ field: string; index?: number; url: string }>[] = [];

      if (formData.thumbnailUrl) {
        imageUploads.push(
          uploadEpisodeAsset(newEpisodeId, 'thumbnail', formData.thumbnailUrl)
            .then((url) => ({ field: 'thumbnailUrl', url }))
            .catch(() => ({ field: 'thumbnailUrl', url: formData.thumbnailUrl! }))
        );
      }
      if (formData.backgroundImageUrl) {
        imageUploads.push(
          uploadEpisodeAsset(newEpisodeId, 'background', formData.backgroundImageUrl)
            .then((url) => ({ field: 'backgroundImageUrl', url }))
            .catch(() => ({ field: 'backgroundImageUrl', url: formData.backgroundImageUrl! }))
        );
      }
      formData.personas.forEach((p, i) => {
        if (p.thumbnailUrl) {
          imageUploads.push(
            uploadEpisodeAsset(newEpisodeId, `persona_${i}`, p.thumbnailUrl)
              .then((url) => ({ field: 'personaThumbnail', index: i, url }))
              .catch(() => ({ field: 'personaThumbnail', index: i, url: p.thumbnailUrl! }))
          );
        }
      });

      const imageResults = await Promise.all(imageUploads);

      // Build persona list with updated thumbnail URLs
      const personas = formData.personas.map((p, i) => {
        const uploaded = imageResults.find((r) => r.field === 'personaThumbnail' && r.index === i);
        return {
          name: p.name,
          role: p.role || p.name,
          description: p.description,
          thumbnailUrl: uploaded?.url || p.thumbnailUrl || null,
        };
      });

      const thumbnailResult = imageResults.find((r) => r.field === 'thumbnailUrl');
      const backgroundResult = imageResults.find((r) => r.field === 'backgroundImageUrl');

      // 2. Create the episode
      await createEpisodeMutation.mutateAsync({
        id: newEpisodeId,
        name: formData.name,
        languageCode: formData.languageCode,
        durationId: formData.durationId,
        context: formData.context,
        goals: formData.goals,
        personas,
        scenario: formData.scenario,
        complexityLevel: formData.complexityLevel,
        tagIds: formData.tagIds,
        thumbnailUrl: thumbnailResult?.url || formData.thumbnailUrl || null,
        backgroundImageUrl: backgroundResult?.url || formData.backgroundImageUrl || null,
        dictionary: formData.dictionary?.length
          ? formData.dictionary.map((d) => ({
              word: d.word,
              explanation: d.explanation,
              examplePhrases: d.examplePhrases,
              localized: d.localized || null,
            }))
          : null,
      });

      // 3. Create display localizations (if any)
      const localizations = exportData.display_localizations || [];
      let locSuccess = 0;
      let locFail = 0;
      for (const loc of localizations) {
        try {
          const transformed = transformLocalizationForApi(loc);
          await createLocalizationMutation.mutateAsync({
            episodeId: newEpisodeId,
            localization: transformed,
          });
          locSuccess++;
        } catch {
          locFail++;
        }
      }

      // 4. Show result
      let message = 'Episode imported successfully!';
      if (locSuccess > 0) {
        message += ` ${locSuccess} localization(s) created.`;
      }
      if (locFail > 0) {
        message += ` ${locFail} localization(s) failed.`;
      }
      toast.success(message, { id: toastId });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Import failed';
      toast.error(errorMsg, { id: toastId });
    } finally {
      setIsImporting(false);
    }
  };

  const handleEdit = (episode: EpisodeListItem) => {
    navigate(`/episodes/${episode.id}/edit`);
  };

  /**
   * Handle Try button click
   * Story 3.9.15: Episodes are language-specific - no language selection needed
   * Just check that voices are assigned before creating session
   */
  const handleTry = async (episode: EpisodeListItem) => {
    // Prevent double-click
    if (isTryButtonLoading) return;

    setIsTryButtonLoading(episode.id);

    try {
      // Story 3.9.15: Check that voices are assigned
      if (episode.voiceGroupsCount === 0) {
        toast.error('This episode has no voices assigned. Assign voices first.');
        setIsTryButtonLoading(null);
        return;
      }

      // Create session directly - no language selection needed
      logger.info('Creating session for episode', {
        episodeId: episode.id,
        languageCode: episode.languageCode,
      });

      const sessionResponse = await createSession(episode.id);
      logger.info('Session created', {
        sessionId: sessionResponse.session_id,
        episodeId: episode.id,
      });

      // Navigate to Try page with session ID
      navigate(`/sessions/${sessionResponse.session_id}`);
    } catch (error) {
      // Check if this is an "active session exists" error
      const axiosError = error as AxiosError<ApiErrorResponse>;
      const errorCode = axiosError.response?.data?.error?.code;
      const existingSessionId = axiosError.response?.data?.error?.extra?.existing_session_id;

      if (errorCode === 'ACTIVE_SESSION_EXISTS' && existingSessionId) {
        logger.info('Active session exists, showing conflict dialog', {
          episodeId: episode.id,
          existingSessionId,
        });
        // Show dialog to let admin choose: continue or start fresh
        setSessionConflict({
          show: true,
          episodeId: episode.id,
          existingSessionId,
          episodeName: episode.name,
        });
      } else {
        logger.error('Failed to handle Try button', { episodeId: episode.id, error });
        toast.error('Failed to prepare episode session. Please try again.');
      }
    } finally {
      setIsTryButtonLoading(null);
    }
  };

  /**
   * Handle "Continue" choice in session conflict dialog
   * Navigates to the existing session
   */
  const handleContinueSession = () => {
    if (sessionConflict.existingSessionId) {
      navigate(`/sessions/${sessionConflict.existingSessionId}`);
    }
    setSessionConflict({ show: false, episodeId: null, existingSessionId: null, episodeName: '' });
  };

  /**
   * Handle "Start Fresh" choice in session conflict dialog
   * Finishes the existing session and creates a new one
   */
  const handleStartFresh = async () => {
    if (!sessionConflict.existingSessionId || !sessionConflict.episodeId) return;

    setIsTryButtonLoading(sessionConflict.episodeId);

    try {
      // First, finish the existing session
      logger.info('Finishing existing session', {
        sessionId: sessionConflict.existingSessionId,
      });
      await finishSession(sessionConflict.existingSessionId);

      // Then create a new session
      logger.info('Creating new session for episode', {
        episodeId: sessionConflict.episodeId,
      });
      const sessionResponse = await createSession(sessionConflict.episodeId);

      // Close dialog and navigate to new session
      setSessionConflict({
        show: false,
        episodeId: null,
        existingSessionId: null,
        episodeName: '',
      });
      navigate(`/sessions/${sessionResponse.session_id}`);
    } catch (error) {
      logger.error('Failed to start fresh session', { error });
      toast.error('Failed to start fresh session. Please try again.');
    } finally {
      setIsTryButtonLoading(null);
    }
  };

  // Story 3.9.15: Navigate to voices assignment
  const handleVoices = (episode: EpisodeListItem) => {
    navigate(`/episodes/${episode.id}/voices`);
  };

  const handleSessions = (episode: EpisodeListItem) => {
    navigate(`/episodes/${episode.id}/sessions`);
  };

  /**
   * Story 11.19: Handle Export JSON button click
   * Fetches display localizations and exports episode data as JSON file
   */
  const handleExport = async (episode: EpisodeListItem) => {
    // Prevent double-click
    if (exportingEpisodeId) return;

    setExportingEpisodeId(episode.id);

    try {
      // Fetch full episode body (list endpoint only returns lean items)
      const fullEpisode = await fetchEpisode(episode.id);

      // Fetch display localizations for this episode
      let localizations: Parameters<typeof exportEpisodeToJson>[1] = [];
      try {
        const rawData = await apiGet<
          {
            id: string;
            episode_id: string;
            language_code: string;
            localized_name: string;
            localized_context: string;
            localized_goals: string[];
            localized_personas: Array<{ name: string; description: string }>;
            created_at: string;
            updated_at: string;
          }[]
        >(`/api/admin/episodes/${episode.id}/display-localizations`);
        // Transform snake_case to camelCase
        localizations = rawData.map((loc) => ({
          id: loc.id,
          episodeId: loc.episode_id,
          languageCode: loc.language_code,
          localizedName: loc.localized_name,
          localizedContext: loc.localized_context,
          localizedGoals: loc.localized_goals,
          localizedPersonas: loc.localized_personas,
          createdAt: loc.created_at,
          updatedAt: loc.updated_at,
        }));
      } catch {
        // No localizations available, continue with empty array
      }

      // Generate export data
      const exportData = exportEpisodeToJson(fullEpisode, localizations, tags);

      // Generate filename: {sanitized_name}_{language_code}.json
      const filename = `${sanitizeFilename(episode.name)}_${episode.languageCode}.json`;

      // Trigger download
      downloadJson(exportData, filename);

      toast.success('Episode exported successfully');
      logger.info('Episode exported', { episodeId: episode.id, filename });
    } catch (error) {
      logger.error('Failed to export episode', { episodeId: episode.id, error });
      toast.error('Failed to export episode. Please try again.');
    } finally {
      setExportingEpisodeId(null);
    }
  };

  const handleToggleStatus = async (episode: EpisodeListItem) => {
    try {
      if (episode.status === 'draft' || episode.status === 'deactivated') {
        await activateEpisodeMutation.mutateAsync(episode.id);
        toast.success('Episode activated successfully!');
      } else if (episode.status === 'active') {
        await deactivateEpisodeMutation.mutateAsync(episode.id);
        toast.success('Episode deactivated successfully!');
      }
    } catch {
      if (episode.status === 'draft' || episode.status === 'deactivated') {
        toast.error('Failed to activate episode. Ensure voices are assigned.');
      } else {
        toast.error('Failed to change episode status');
      }
    }
  };

  const handleDelete = (episode: EpisodeListItem) => {
    setEpisodeToDelete(episode);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!episodeToDelete) return;

    try {
      await deleteEpisodeMutation.mutateAsync(episodeToDelete.id);
      toast.success('Episode deleted successfully!');
      setShowDeleteConfirm(false);
      setEpisodeToDelete(null);
    } catch {
      toast.error('Failed to delete episode');
    }
  };

  // Client-side search filter
  const filteredEpisodes =
    episodes?.filter((episode) =>
      episode.name.toLowerCase().includes(debouncedSearch.toLowerCase())
    ) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading episodes</p>
          <p className="text-gray-600">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <EpisodeNavigation />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Episode Management</h1>
        <div className="flex space-x-3">
          <Link
            to="/episodes/bulk-generate"
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Bulk Generate
          </Link>
          <CreateEpisodeButton
            onExecute={handleExecuteCreation}
            onMethodChange={handleMethodChange}
            currentMethod={creationMethod}
            isImporting={isImporting}
          />
        </div>
      </div>

      {/* Hidden file input for JSON import */}
      <input
        type="file"
        ref={importFileInputRef}
        onChange={handleImportFileChange}
        accept=".json,application/json"
        className="hidden"
        data-testid="import-file-input"
      />

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search */}
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name..."
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="search-input"
          />

          {/* Status Filter */}
          <select
            value={filters.status || ''}
            onChange={(e) =>
              handleFilterChange('status', e.target.value as EpisodeStatus | undefined)
            }
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="status-filter"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="deactivated">Deactivated</option>
          </select>

          {/* Complexity Filter (multi-select) */}
          <select
            multiple
            value={filters.complexity?.map(String) || []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, (option) =>
                parseInt(option.value)
              );
              handleFilterChange('complexity', selected.length > 0 ? selected : undefined);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="complexity-filter"
          >
            {skillLevels.map((skillLevel) => (
              <option key={skillLevel.id} value={skillLevel.id}>
                {getSkillLevelEnglishName(skillLevel)}
              </option>
            ))}
          </select>

          {/* Learning Goal Filter (single-select, filters tags) */}
          <select
            value={selectedLearningGoalId || ''}
            onChange={(e) => handleLearningGoalChange(e.target.value || undefined)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="learning-goal-filter"
          >
            <option value="">All Learning Goals</option>
            {learningGoals.map((goal) => (
              <option key={goal.id} value={goal.id}>
                {getLearningGoalEnglishName(goal)}
              </option>
            ))}
          </select>

          {/* Tags Filter (multi-select, filtered by selected learning goal) */}
          <select
            multiple
            value={filters.tags || []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, (option) => option.value);
              handleFilterChange('tags', selected.length > 0 ? selected : undefined);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="tags-filter"
          >
            {filteredTags.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Empty State */}
      {filteredEpisodes.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center" data-testid="empty-state">
          <p className="text-gray-600 text-lg mb-4">
            No episodes found. Create your first episode to get started.
          </p>
          <div className="flex justify-center">
            <CreateEpisodeButton
              onExecute={handleExecuteCreation}
              onMethodChange={handleMethodChange}
              currentMethod={creationMethod}
              isImporting={isImporting}
            />
          </div>
        </div>
      ) : (
        <>
          {/* Episodes Table */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200" data-testid="episodes-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Personas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tags
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Complexity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Voices
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Updated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredEpisodes.map((episode) => (
                  <tr key={episode.id} data-testid={`episode-row-${episode.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{episode.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{episode.personasCount}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {episode.tagIds.map((tagId) => {
                          const tag = tags.find((t) => t.id === tagId);
                          return tag ? (
                            <span
                              key={tagId}
                              className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded"
                            >
                              {tag.name}
                            </span>
                          ) : null;
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        const skillLevel = skillLevels.find(
                          (sl) => sl.id === episode.complexityLevel
                        );
                        return skillLevel ? (
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                            {getSkillLevelEnglishName(skillLevel)}
                          </span>
                        ) : null;
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-1 text-xs rounded ${
                            episode.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : episode.status === 'draft'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {episode.status.charAt(0).toUpperCase() + episode.status.slice(1)}
                        </span>
                        <button
                          onClick={() => handleToggleStatus(episode)}
                          className={`px-2 py-1 text-xs rounded ${
                            episode.status === 'active'
                              ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                              : 'bg-green-600 hover:bg-green-700 text-white'
                          }`}
                          data-testid="toggle-status-button"
                          title={
                            episode.status === 'active'
                              ? 'Deactivate episode'
                              : 'Activate episode (requires voices)'
                          }
                        >
                          {episode.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        {episode.voiceGroupsCount > 0 ? (
                          <span className="text-green-600">
                            {episode.voiceGroupsCount} group
                            {episode.voiceGroupsCount !== 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span className="text-red-600">Not assigned</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {formatRelativeTime(episode.updatedAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex flex-col space-y-1">
                        <button
                          onClick={() => handleEdit(episode)}
                          className="text-blue-600 hover:text-blue-800 text-left"
                          data-testid="edit-button"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleTry(episode)}
                          disabled={
                            episode.voiceGroupsCount === 0 || isTryButtonLoading === episode.id
                          }
                          className={`text-left ${
                            episode.voiceGroupsCount === 0
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-green-600 hover:text-green-800'
                          }`}
                          data-testid="try-button"
                          title={
                            episode.voiceGroupsCount === 0
                              ? 'Episode must have voices assigned to be tested'
                              : 'Test this episode with WebRTC conversation'
                          }
                        >
                          {isTryButtonLoading === episode.id ? 'Loading...' : 'Try'}
                        </button>
                        <button
                          onClick={() => handleVoices(episode)}
                          className="text-purple-600 hover:text-purple-800 text-left"
                          data-testid="voices-button"
                        >
                          Voices
                        </button>
                        <button
                          onClick={() => navigate(`/episodes/${episode.id}/localizations`)}
                          className="text-teal-600 hover:text-teal-800 text-left"
                          data-testid="localizations-button"
                        >
                          Localizations
                        </button>
                        <button
                          onClick={() => handleSessions(episode)}
                          className="text-orange-600 hover:text-orange-800 text-left"
                          data-testid="sessions-button"
                        >
                          Sessions
                        </button>
                        <button
                          onClick={() => handleExport(episode)}
                          disabled={exportingEpisodeId === episode.id}
                          className={`text-left ${
                            exportingEpisodeId === episode.id
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-cyan-600 hover:text-cyan-800'
                          }`}
                          data-testid="export-button"
                          title="Export episode as JSON file"
                        >
                          {exportingEpisodeId === episode.id ? 'Exporting...' : 'Export JSON'}
                        </button>
                        <button
                          onClick={() => handleDelete(episode)}
                          className="text-red-600 hover:text-red-800 text-left"
                          data-testid="delete-button"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Page {page} - Showing {filteredEpisodes.length} episodes
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                data-testid="prev-page-button"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={filteredEpisodes.length < 50}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                data-testid="next-page-button"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && episodeToDelete && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          data-testid="delete-confirmation-dialog"
        >
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{episodeToDelete.name}"?{' '}
              {episodeToDelete.status === 'draft'
                ? 'This will permanently delete the episode.'
                : 'This will soft-delete the episode.'}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setEpisodeToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                data-testid="cancel-delete-button"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteEpisodeMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:bg-red-400"
                data-testid="confirm-delete-button"
              >
                {deleteEpisodeMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Conflict Dialog - shown when active session already exists */}
      {sessionConflict.show && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          data-testid="session-conflict-dialog"
        >
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md relative">
            {/* Close button */}
            <button
              onClick={() =>
                setSessionConflict({
                  show: false,
                  episodeId: null,
                  existingSessionId: null,
                  episodeName: '',
                })
              }
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="close-session-conflict-button"
              aria-label="Close dialog"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h3 className="text-lg font-semibold mb-4">Active Session Exists</h3>
            <p className="text-gray-600 mb-6">
              You already have an active session for "{sessionConflict.episodeName}". Would you like
              to continue the existing session or start fresh?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleStartFresh}
                disabled={isTryButtonLoading === sessionConflict.episodeId}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors disabled:bg-orange-400"
                data-testid="start-fresh-button"
              >
                {isTryButtonLoading === sessionConflict.episodeId ? 'Starting...' : 'Start Fresh'}
              </button>
              <button
                onClick={handleContinueSession}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                data-testid="continue-session-button"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
